import React, { useState, useEffect } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import Swal from "sweetalert2";

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm();

  // Fetch employees from API
  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get("https://ma-laravel.ma-uae.com/api/employees");
      setEmployees(response.data.data); // Access data inside "data"
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch employees. Please try again.",
      });
    }
  };

  // Handle form submission for adding/editing employee
  const onSubmit = async (data) => {
    const formData = new FormData();
    formData.append("name", data.name);
    formData.append("position", data.position || "");
    formData.append("type", data.type);
    formData.append("brief", data.brief || "");
    if (data.image && data.image[0]) {
      formData.append("image", data.image[0]);
    }

    try {
      if (isEditing) {
        // Update employee using POST with _method=PUT for multipart/form-data
        await axios.post(
          `https://ma-laravel.ma-uae.com/api/employees/${currentEmployee.id}?_method=PUT`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Employee updated successfully!",
        });
      } else {
        // Add new employee
        await axios.post("https://ma-laravel.ma-uae.com/api/employees", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Employee added successfully!",
        });
      }
      fetchEmployees();
      setIsModalOpen(false);
      reset();
      setIsEditing(false);
      setCurrentEmployee(null);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.message ||
          Object.values(error.response?.data?.errors || {}).flat().join(" ") ||
          "Failed to save employee. Please try again.",
      });
    }
  };

  // Handle delete with SweetAlert2
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action will delete the employee permanently.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OK",
      cancelButtonText: "Close",
    });

    if (result.isConfirmed) {
      try {
        await axios.delete(`https://ma-laravel.ma-uae.com/api/employees/${id}`);
        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: "Employee deleted successfully!",
        });
        fetchEmployees();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to delete employee. Please try again.",
        });
      }
    }
  };

  // Handle edit
  const handleEdit = (employee) => {
    setIsEditing(true);
    setCurrentEmployee(employee);
    setValue("name", employee.name);
    setValue("position", employee.position || "");
    setValue("type", employee.type);
    setValue("brief", employee.brief || "");
    setIsModalOpen(true);
  };

  return (
    <section className="p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold mb-4">Employees</h1>
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded mb-4 hover:bg-blue-600"
          onClick={() => {
            setIsEditing(false);
            reset();
            setIsModalOpen(true);
          }}
        >
          Add Employee
        </button>
      </div>

      {/* Employees Table */}
      <table className="w-full border-collapse border border-gray-300">
        <thead>
          <tr className="bg-gray-100">
            <th className="border border-gray-300 p-2">Image</th>
            <th className="border border-gray-300 p-2">Name</th>
            <th className="border border-gray-300 p-2">Position</th>
            <th className="border border-gray-300 p-2">Type</th>
            <th className="border border-gray-300 p-2">Brief</th>
            <th className="border border-gray-300 p-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {employees.map((employee) => (
            <tr key={employee.id}>
              <td className="border border-gray-300 p-2">
                {employee.image_path ? (
                  <img
                    src={`https://ma-laravel.ma-uae.com/storage/app/public/${employee.image_path}`}
                    alt={employee.name}
                    className="w-16 h-16 object-cover"
                  />
                ) : (
                  "No Image"
                )}
              </td>
              <td className="border border-gray-300 p-2">{employee.name}</td>
              <td className="border border-gray-300 p-2">{employee.position || "Null"}</td>
              <td className="border border-gray-300 p-2">{employee.type}</td>
              <td className="border border-gray-300 p-2">{employee.brief || "Null"}</td>
              <td className="border border-gray-300 p-2">
                <button
                  className="bg-yellow-500 text-white px-2 py-1 rounded mr-2 hover:bg-yellow-600"
                  onClick={() => handleEdit(employee)}
                >
                  Edit
                </button>
                <button
                  className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600"
                  onClick={() => handleDelete(employee.id)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Modal for Add/Edit Employee */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-1/2">
            <h2 className="text-xl font-bold mb-4">
              {isEditing ? "Edit Employee" : "Add Employee"}
            </h2>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-4">
                <label className="block text-sm font-medium">Name</label>
                <input
                  type="text"
                  {...register("name", { required: "Name is required" })}
                  className="w-full border border-gray-300 p-2 rounded"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm">{errors.name.message}</p>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium">Position</label>
                <input
                  type="text"
                  {...register("position")}
                  className="w-full border border-gray-300 p-2 rounded"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium">Type</label>
                <select
                  {...register("type", { required: "Type is required" })}
                  className="w-full border border-gray-300 p-2 rounded"
                >
                  <option value="">Select Type</option>
                  <option value="ceo">CEO</option>
                  <option value="employee">Employee</option>
                </select>
                {errors.type && (
                  <p className="text-red-500 text-sm">{errors.type.message}</p>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium">Brief</label>
                <textarea
                  {...register("brief", {
                    validate: (value, formValues) =>
                      formValues.type === "ceo"
                        ? value
                          ? true
                          : "Brief is required for CEO"
                        : true,
                  })}
                  className="w-full border border-gray-300 p-2 rounded"
                />
                {errors.brief && (
                  <p className="text-red-500 text-sm">{errors.brief.message}</p>
                )}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium">Image</label>
                <input
                  type="file"
                  accept="image/*"
                  {...register("image", {
                    required: !isEditing && "Image is required",
                  })}
                  className="w-full border border-gray-300 p-2 rounded"
                />
                {errors.image && (
                  <p className="text-red-500 text-sm">{errors.image.message}</p>
                )}
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  className="bg-gray-500 text-white px-4 py-2 rounded mr-2 hover:bg-gray-600"
                  onClick={() => {
                    setIsModalOpen(false);
                    reset();
                    setIsEditing(false);
                    setCurrentEmployee(null);
                  }}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                >
                  OK
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}